import React, { useState, useEffect } from "react";
import { EditButton, ShowButton, DeleteButton } from "@refinedev/antd";
import { useList } from "@refinedev/core";
import { Table, Space, Tag, Card, Input } from "antd";
import { SearchOutlined, PlusOutlined, CalendarOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../../../components/ui";

interface AccountingPeriod {
    id: number;
    periodId: string;
    year: number;
    month: number;
    startDate: string;
    endDate: string;
    status: number;
    closedDate?: string;
    createdAt: string;
}

export const AccountingPeriodsList = () => {
    const [searchText, setSearchText] = useState<string>("");
    const [filteredItems, setFilteredItems] = useState<AccountingPeriod[]>([]);
    const navigate = useNavigate();

    const { data, isLoading } = useList({
        resource: "accounting-periods",
    });

    useEffect(() => {
        if (!data?.data) return;

        let periods = data.data as AccountingPeriod[];

        if (searchText.trim() !== "") {
            const searchLower = searchText.toLowerCase();
            periods = periods.filter((period: AccountingPeriod) =>
                period.periodId?.toLowerCase().includes(searchLower) ||
                period.year?.toString().includes(searchLower)
            );
        }

        const sorted = [...periods].sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setFilteredItems(sorted);
    }, [data, searchText]);

    const getStatusColor = (status: number) => {
        switch (status) {
            case 0: return "warning";
            case 1: return "success";
            case 2: return "error";
            default: return "default";
        }
    };

    const getStatusText = (status: number) => {
        switch (status) {
            case 0: return "Open";
            case 1: return "Closed";
            case 2: return "Locked";
            default: return "Unknown";
        }
    };

    const columns = [
        {
            title: "Period ID",
            dataIndex: "periodId",
            key: "periodId",
            render: (value: string) => (
                <span className="font-medium text-text-primary">{value}</span>
            ),
        },
        {
            title: "Year",
            dataIndex: "year",
            key: "year",
            render: (value: number) => (
                <span className="text-text-secondary">{value}</span>
            ),
        },
        {
            title: "Month",
            dataIndex: "month",
            key: "month",
            render: (value: number) => (
                <span className="text-text-secondary">{value}</span>
            ),
        },
        {
            title: "Start Date",
            dataIndex: "startDate",
            key: "startDate",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {value ? new Date(value).toLocaleDateString() : "-"}
                </span>
            ),
            responsive: ['md'] as any,
        },
        {
            title: "End Date",
            dataIndex: "endDate",
            key: "endDate",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {value ? new Date(value).toLocaleDateString() : "-"}
                </span>
            ),
            responsive: ['md'] as any,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (value: number) => (
                <Tag color={getStatusColor(value)} className="status-tag">
                    {getStatusText(value)}
                </Tag>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            render: (_: any, record: AccountingPeriod) => (
                <Space size="small">
                    <EditButton hideText size="small" recordItemId={record.id} />
                    <ShowButton hideText size="small" recordItemId={record.id} />
                    <DeleteButton hideText size="small" recordItemId={record.id} />
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title="Accounting Periods"
                icon={<CalendarOutlined />}
                breadcrumbs={[{ title: "Accounting" }, { title: "Periods" }]}
                actions={[
                    {
                        label: "Create",
                        onClick: () => navigate("/accounting-periods/create"),
                        type: "primary",
                        icon: <PlusOutlined />,
                    },
                ]}
            />

            <div className="data-table-card">
                <Card>
                    <div style={{ marginBottom: 16 }}>
                        <Input
                            placeholder="Search by period ID or year"
                            prefix={<SearchOutlined className="text-text-muted" />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ maxWidth: 400 }}
                            allowClear
                            size="large"
                        />
                    </div>

                    <Table
                        dataSource={filteredItems}
                        columns={columns}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        }}
                        className="custom-table"
                        scroll={{ x: 800 }}
                    />
                </Card>
            </div>
        </div>
    );
};
